import { supabase } from '../lib/supabase'

/**
 * The lot load fee.
 *
 * Winnie, 13 September: "Add in Load fee - dollar amount to be able manually set and apply
 * across all outflow deals. The fixed Lot Load Fund is $250 as auto-applied the moment a
 * vehicle reaches Paid - Ready for Sale, same trigger point as the other inflow costs, so
 * nobody has to remember to add it per vehicle."
 *
 * Two halves, and they are separate on purpose:
 *
 *   - the amount is a setting, typed once in Admin, so raising it from $250 next quarter is
 *     one field rather than a code change;
 *   - the application is automatic at `paid`, so it lands on every car without anybody
 *     remembering. A cost that has to be remembered per vehicle is a cost that is missing
 *     from the margin on the vehicles nobody remembered.
 *
 * It is a real expenses row rather than a number the reports subtract, because the
 * commission engine strikes landed cost from the cost lines on a vehicle (Commission spec
 * v2, section 2). A fee that existed only in a report would be visible on screen and absent
 * from every commission figure.
 */

/** What the fee is, unless Admin says otherwise. Winnie's figure. */
export const DEFAULT_LOT_LOAD_FEE = 250

/** The cost line it writes. One place, so the idempotency check cannot drift from the write. */
const LOT_LOAD_CATEGORY = 'lot_load'

export async function loadLotLoadFee(): Promise<number> {
  const { data, error } = await supabase
    .from('admin_settings')
    .select('lot_load_fee')
    .limit(1)
    .maybeSingle()
  if (error) return DEFAULT_LOT_LOAD_FEE
  const raw = (data as { lot_load_fee?: number | null } | null)?.lot_load_fee
  return raw === null || raw === undefined ? DEFAULT_LOT_LOAD_FEE : Number(raw)
}

export interface LotLoadResult {
  applied: boolean
  amount: number
  message: string
}

/**
 * Charge the lot load fee to a vehicle, once.
 *
 * Idempotent by design: it looks for a lot load line on this deal before writing one. A
 * vehicle can reach `paid` more than once - a payment reversed and re-approved, a status
 * corrected by hand - and a fee that charged again each time would quietly eat the margin
 * on exactly the deals that had already been through trouble.
 *
 * A fee of zero applies nothing. Setting the amount to 0 is how the business turns the fee
 * off, and writing $0.00 cost rows against every car would be a strange way to honour that.
 */
export async function applyLotLoadFee(dealId: string, userId: string | null = null): Promise<LotLoadResult> {
  const amount = await loadLotLoadFee()
  if (!(amount > 0)) {
    return { applied: false, amount: 0, message: 'The lot load fee is set to zero, so nothing was charged.' }
  }

  const { data: existing, error: findError } = await supabase
    .from('expenses')
    .select('id')
    .eq('deal_id', dealId)
    .eq('category', LOT_LOAD_CATEGORY)
    .limit(1)
    .maybeSingle()
  if (findError) throw findError
  if (existing) {
    return { applied: false, amount, message: 'This vehicle already carries a lot load fee.' }
  }

  const today = new Date().toISOString().slice(0, 10)
  const { error } = await supabase.from('expenses').insert({
    deal_id: dealId,
    category: LOT_LOAD_CATEGORY,
    cost_type: 'LotLoad',
    description: 'Lot load fee — applied automatically when the vehicle became ready for sale.',
    amount,
    /* GST inclusive, like every other row on this ledger: the fee is an internal charge
       carrying GST at 1/11th of the gross, the same arithmetic the Cost of Sales form does. */
    gst_amount: Math.round((amount / 11) * 100) / 100,
    supplier: 'Direct Auto',
    paid_to: 'Direct Auto',
    incurred_on: today,
    capitalised: true,
    /* An internal charge, not a supplier bill: there is no invoice to wait for, so it is
       Received from the moment it is applied rather than sitting as an unreconciled
       estimate on the Standard Costs chase list forever. */
    bill_status: 'Received',
    tax_invoice: false,
    payment_status: 'paid',
    paid_at: new Date().toISOString(),
    created_by: userId,
  })
  if (error) throw error

  return { applied: true, amount, message: `Lot load fee of $${amount.toFixed(2)} applied.` }
}
